import React, { useState, useEffect, forwardRef } from 'react';
import { getBatchEditValue, DatatypeCategory } from '../helpers';
import { Checkbox, Input, Select } from '../components';
import { useGlobalState, useMessage } from '../hooks';

const _AddDatatypeForm = (
  { mode, data, submitForm, loading, setLoading, formErrors = {} },
  ref
) => {
  const initialFormState = {
    datatype_id: null,
    datatype_name: '',
    datatype_category: '',
    has_length: false,
    has_precision: false,
    has_scale: false,
    has_children: false,
  };

  const initialBatchEditItems = {
    batch_datatype_category: '',
    batch_has_length: false,
    batch_has_precision: false,
    batch_has_scale: false,
    batch_has_children: false,
  };

  const [formData, setFormData] = useState(initialFormState);
  const [batchEdit, setBatchEdit] = useState(false);
  const [batchEditItems, setBatchEditItems] = useState(initialBatchEditItems);

  const [displayNames] = useGlobalState('displayNames');
  const { hideMessage } = useMessage();

  useEffect(() => {
    setBatchEdit(false);
    if (data.length === 1) {
      setFormData(prev => ({
        datatype_id: data[0].datatype_id,
        datatype_name: data[0].datatype_name || prev.datatype_name,
        datatype_category: data[0].datatype_category || prev.datatype_category,
        has_length: data[0].has_length || prev.has_length,
        has_precision: data[0].has_precision || prev.has_precision,
        has_scale: data[0].has_scale || prev.has_scale,
        has_children: data[0].has_children || prev.has_children,
      }));
    } else if (data.length > 1 && mode !== 'add') {
      setBatchEdit(true);

      setFormData({
        datatype_category: getBatchEditValue(data, 'datatype_category'),
        has_length: getBatchEditValue(data, 'has_length'),
        has_precision: getBatchEditValue(data, 'has_precision'),
        has_scale: getBatchEditValue(data, 'has_scale'),
        has_children: getBatchEditValue(data, 'has_children'),
      });
      //} else if(mode === 'add') {
    } else {
      resetForm();
    }
  }, [mode, data]);

  const handleInputChange = (event, formPart) => {
    let { name, value } = '';

    // Handle standard form inputs
    if (event.target !== undefined) {
      name = event.target.name;
      value = event.target.value;

      // If input element is a checkbox, we cannot use "value"
      if (event.target.type === 'checkbox') {
        value = event.target.checked;
      }

      // Handle custom form inputs
    } else {
      name = event.name;
      value = event.value;
    }

    switch (formPart) {
      case 'batch':
        setBatchEditItems({ ...batchEditItems, [name]: value });
        break;
      default:
        setFormData({ ...formData, [name]: value });
    }

    // Hide errors on input change
    if (formErrors && formErrors[name]) {
      delete formErrors[name];
      hideMessage();
    }
  };

  const onSubmit = event => {
    event.preventDefault();

    setLoading(true);

    // Form validation
    //if (!formData.user_fullname || !formData.user_username) return

    let dataToSubmit;

    if (batchEdit) {
      let key = '';
      let tmp = {};
      const objects = Object.entries(batchEditItems);

      for (let i = 0; i < objects.length; i++) {
        if (objects[i][1]) {
          key = objects[i][0].replace('batch_', '');
          tmp = { ...tmp, [key]: formData[key] };
        }
      }

      dataToSubmit = data.map(item => {
        return {
          datatype_id: item.datatype_id,
          datatype_name: item.datatype_name,
          datatype_category: item.datatype_category,
          ...tmp,
        };
      });
    } else {
      dataToSubmit = formData;
    }

    // submit form
    submitForm(dataToSubmit)
      .then(() => resetForm())
      .catch(() => {
        // Errors handled in submitForm function
      })
      .finally(() => setLoading(false));
  };

  const resetForm = () => {
    setBatchEdit(false);
    setBatchEditItems(initialBatchEditItems);
    setFormData(prevFormData => {
      return { ...initialFormState };
    });
  };
  return (
    <div className="form">
      <form onSubmit={onSubmit} ref={ref}>
        {!batchEdit && (
          <div className="form-block vertical">
            <Input
              type="text"
              name="datatype_name"
              value={formData.datatype_name}
              onChange={handleInputChange}
              disabled={loading}
              validation={
                formErrors.datatype_name
                  ? { valid: false, message: formErrors.datatype_name }
                  : undefined
              }
            />
          </div>
        )}
        <div className="form-block vertical">
          {batchEdit && (
            <label>
              <Checkbox
                checked={batchEditItems.batch_datatype_category}
                name="batch_datatype_category"
                label="Edit Category"
                onChange={e => handleInputChange(e, 'batch')}
                disabled={loading}
              />
            </label>
          )}
          <Select
            name="datatype_category"
            label={!batchEdit}
            value={
              formData.datatype_category === 'multiple' ? undefined : formData.datatype_category
            }
            options={DatatypeCategory}
            onChange={handleInputChange}
            title="The category defines the icon of the datatype"
            placeholder={
              formData.datatype_category === 'multiple' ? '< Multiple >' : 'Select category...'
            }
            disabled={loading || (batchEdit && !batchEditItems.batch_datatype_category)}
            validation={
              formErrors.datatype_category
                ? { valid: false, message: formErrors.datatype_category }
                : undefined
            }
          />
        </div>

        <div className="form-block vertical">
          {batchEdit ? (
            <label>
              <Checkbox
                checked={batchEditItems.batch_has_length}
                name="batch_has_length"
                label="Edit Length"
                onChange={e => handleInputChange(e, 'batch')}
                disabled={loading}
              />
            </label>
          ) : null}

          <div className="horizontal">
            <Checkbox
              name="has_length"
              checked={formData.has_length}
              onChange={handleInputChange}
              label="Has Length"
              title={displayNames?.has_length?.display_name_description}
              disabled={loading || (batchEdit && !batchEditItems.batch_has_length)}
            />
          </div>
        </div>

        <div className="form-block vertical">
          {batchEdit ? (
            <label>
              <Checkbox
                checked={batchEditItems.batch_has_precision}
                name="batch_has_precision"
                label="Edit Precision"
                onChange={e => handleInputChange(e, 'batch')}
                disabled={loading}
              />
            </label>
          ) : null}

          <div className="horizontal">
            <Checkbox
              name="has_precision"
              checked={formData.has_precision}
              onChange={handleInputChange}
              label="Has Precision"
              title={displayNames?.has_precision?.display_name_description}
              disabled={loading || (batchEdit && !batchEditItems.batch_has_precision)}
            />
          </div>
        </div>

        <div className="form-block vertical">
          {batchEdit ? (
            <label>
              <Checkbox
                checked={batchEditItems.batch_has_scale}
                name="batch_has_scale"
                label="Edit Scale"
                onChange={e => handleInputChange(e, 'batch')}
                disabled={loading}
              />
            </label>
          ) : null}

          <div className="horizontal">
            <Checkbox
              name="has_scale"
              checked={formData.has_scale}
              onChange={handleInputChange}
              label="Has Scale"
              title={displayNames?.has_scale?.display_name_description}
              disabled={loading || (batchEdit && !batchEditItems.batch_has_scale)}
            />
          </div>
        </div>

        <div className="form-block vertical">
          {batchEdit ? (
            <label>
              <Checkbox
                checked={batchEditItems.batch_has_children}
                name="batch_has_children"
                label="Edit Children"
                onChange={e => handleInputChange(e, 'batch')}
                disabled={loading}
              />
            </label>
          ) : null}

          <div className="horizontal">
            <Checkbox
              name="has_children"
              checked={formData.has_children}
              onChange={handleInputChange}
              label="Can Have Children / Nested Fields"
              title={displayNames?.has_children?.display_name_description}
              disabled={loading || (batchEdit && !batchEditItems.batch_has_children)}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export const AddDatatypeForm = forwardRef(_AddDatatypeForm);
